import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import type { CredentialVerifyResult, CredentialView } from '@agentic-os/contracts';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button, ErrorNote, Mono, PageHeader, Table, Td, Th } from '@/components/ui/primitives';
import { DomainStatus, StatusBadge } from '@/components/ui/status-badge';
import { api, formatTime } from '@/lib/api';
import { useSession } from '@/lib/session';

const input = 'w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm';

/**
 * Credentials are WRITE-ONLY: values are typed once, sent to the API, and never displayed or
 * returned again. The form clears the value from component state immediately after submit.
 */
export function CredentialsPage() {
  const { can } = useSession();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['credentials'],
    queryFn: () => api<{ items: CredentialView[] }>('/api/v1/credentials'),
  });
  const [form, setForm] = useState({ name: '', kind: 'api_token', description: '', value: '' });
  const [verifyResult, setVerifyResult] = useState<CredentialVerifyResult | null>(null);
  const [editing, setEditing] = useState<CredentialView | null>(null);
  const [edit, setEdit] = useState({ description: '', value: '' });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['credentials'] });

  const create = useMutation({
    mutationFn: (body: typeof form) =>
      api<CredentialView>('/api/v1/credentials', { method: 'POST', body }),
    onSuccess: refresh,
  });
  const action = useMutation({
    mutationFn: ({
      id,
      op,
      value,
    }: {
      id: string;
      op: 'rotate' | 'revoke' | 'verify';
      value?: string;
    }) =>
      api<CredentialView | CredentialVerifyResult>(`/api/v1/credentials/${id}/${op}`, {
        method: 'POST',
        ...(value ? { body: { value } } : {}),
      }),
    onSuccess: (data, vars) => {
      if (vars.op === 'verify') setVerifyResult(data as CredentialVerifyResult);
      refresh();
    },
  });

  /** Saves the description and, when a new value was typed, rotates to it (write-only). */
  const saveEdit = useMutation({
    mutationFn: async (a: { c: CredentialView; description: string; value: string }) => {
      if (a.description !== a.c.description)
        await api<CredentialView>(`/api/v1/credentials/${a.c.id}`, {
          method: 'PUT',
          body: { description: a.description },
        });
      if (a.value)
        await api<CredentialView>(`/api/v1/credentials/${a.c.id}/rotate`, {
          method: 'POST',
          body: { value: a.value },
        });
    },
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
  });
  const submitEdit = (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const body = { c: editing, ...edit };
    setEdit((x) => ({ ...x, value: '' })); // never keep the secret around
    saveEdit.mutate(body);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body = { ...form };
    setForm((f) => ({ ...f, value: '' })); // never keep the secret around
    create.mutate(body);
  };

  const manage = can('credentials.manage');
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Credentials"
        description="Envelope-encrypted, write-only secrets for connections. Values are never shown again after they are saved."
      />
      {manage && (
        <Card>
          <CardHeader>
            <CardTitle>Add credential</CardTitle>
          </CardHeader>
          <CardBody>
            <form onSubmit={submit} className="grid gap-3 md:grid-cols-4" autoComplete="off">
              <label className="text-xs text-muted">
                Name
                <input
                  className={input}
                  required
                  pattern="[a-z0-9][a-z0-9._\-]{1,62}"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </label>
              <label className="text-xs text-muted">
                Kind
                <select
                  className={input}
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                >
                  <option value="api_token">API token</option>
                  <option value="password">Password</option>
                  <option value="oauth_client_secret">OAuth client secret</option>
                  <option value="generic">Generic</option>
                </select>
              </label>
              <label className="text-xs text-muted">
                Description
                <input
                  className={input}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </label>
              <label className="text-xs text-muted">
                Value (write-only)
                <input
                  className={input}
                  type="password"
                  required
                  autoComplete="new-password"
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                />
              </label>
              <div className="md:col-span-4">
                <Button type="submit" variant="primary" disabled={create.isPending}>
                  <KeyRound className="size-3.5" aria-hidden />
                  Save credential
                </Button>
              </div>
            </form>
            {create.isError && (
              <div className="mt-3">
                <ErrorNote error={create.error} />
              </div>
            )}
          </CardBody>
        </Card>
      )}
      {verifyResult && (
        <p role="status" className="flex items-center gap-2 text-sm">
          <StatusBadge
            status={verifyResult.decryptable && verifyResult.fingerprintMatches ? 'ok' : 'fail'}
            label={
              verifyResult.decryptable && verifyResult.fingerprintMatches
                ? 'Decryptable and intact'
                : 'Verification failed'
            }
          />
          version {verifyResult.version}
        </p>
      )}
      {action.isError && <ErrorNote error={action.error} />}
      {manage && editing && (
        <Card>
          <CardHeader>
            <CardTitle>Edit credential {editing.name}</CardTitle>
          </CardHeader>
          <CardBody>
            <form onSubmit={submitEdit} className="grid gap-3 md:grid-cols-3" autoComplete="off">
              <label className="text-xs text-muted">
                Description
                <input
                  className={input}
                  value={edit.description}
                  onChange={(e) => setEdit({ ...edit, description: e.target.value })}
                />
              </label>
              <label className="text-xs text-muted md:col-span-2">
                New value (write-only; leave empty to keep the current value)
                <input
                  className={input}
                  type="password"
                  autoComplete="new-password"
                  value={edit.value}
                  onChange={(e) => setEdit({ ...edit, value: e.target.value })}
                />
              </label>
              <p className="text-xs text-muted md:col-span-3">
                A new value creates version {editing.currentVersion + 1}; connections using this
                credential use it immediately. The name and kind are fixed.
              </p>
              <div className="flex gap-2 md:col-span-3">
                <Button type="submit" variant="primary" disabled={saveEdit.isPending}>
                  <KeyRound className="size-3.5" aria-hidden /> Save changes
                </Button>
                <Button onClick={() => setEditing(null)}>Cancel</Button>
              </div>
            </form>
            {saveEdit.isError && (
              <div className="mt-3">
                <ErrorNote error={saveEdit.error} />
              </div>
            )}
          </CardBody>
        </Card>
      )}
      <Card>
        <CardBody className="p-0">
          {list.isError ? (
            <div className="p-4">
              <ErrorNote error={list.error} />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Kind</Th>
                  <Th>Status</Th>
                  <Th>Version</Th>
                  <Th>Fingerprint</Th>
                  <Th>Last used</Th>
                  <Th>Updated</Th>
                  {manage && <Th>Actions</Th>}
                </tr>
              </thead>
              <tbody>
                {(list.data?.items ?? []).map((c) => (
                  <tr key={c.id}>
                    <Td>
                      {c.name}
                      {c.description && <p className="text-xs text-muted">{c.description}</p>}
                    </Td>
                    <Td>
                      <Mono>{c.kind}</Mono>
                    </Td>
                    <Td>
                      <DomainStatus value={c.status} />
                    </Td>
                    <Td className="tabular-nums">{c.currentVersion}</Td>
                    <Td>
                      <Mono>{c.fingerprint}</Mono>
                    </Td>
                    <Td className="text-muted">{formatTime(c.lastUsedAt)}</Td>
                    <Td className="text-muted">{formatTime(c.updatedAt)}</Td>
                    {manage && (
                      <Td className="whitespace-nowrap">
                        {c.status === 'active' && (
                          <div className="flex gap-1.5">
                            <Button onClick={() => action.mutate({ id: c.id, op: 'verify' })}>
                              Verify
                            </Button>
                            <Button
                              aria-label={`Edit ${c.name}`}
                              onClick={() => {
                                setEditing(c);
                                saveEdit.reset();
                                setEdit({ description: c.description, value: '' });
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="danger"
                              onClick={() =>
                                window.confirm(`Revoke ${c.name}? This cannot be undone.`) &&
                                action.mutate({ id: c.id, op: 'revoke' })
                              }
                            >
                              Revoke
                            </Button>
                          </div>
                        )}
                      </Td>
                    )}
                  </tr>
                ))}
                {list.data?.items.length === 0 && (
                  <tr>
                    <Td colSpan={manage ? 8 : 7} className="text-muted">
                      No credentials.
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
